import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { I18nLoaderService } from './i18n-loader';
import { DetectedLocale, LanguageService } from './language';

export const READY_TIMEOUT_MS = 4000;

@Injectable({
  providedIn: 'root',
})
export class LanguageReadyService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly languageService = inject(LanguageService);
  private readonly translate = inject(TranslateService);
  private readonly i18n = inject(I18nLoaderService);

  private readonly _ready = signal(false);
  private pending = 0;
  private detection?: Promise<DetectedLocale>;
  private readonly parts = new Map<string, Promise<void>>();

  readonly ready = this._ready.asReadonly();

  /**
   * Detecta o idioma e aplica (uma vez por aplicação) e carrega só o `part` que ainda falta.
   * No servidor não faz nada: o ready fica false e o skeleton vai no HTML inicial.
   */
  async ensure(part: string): Promise<DetectedLocale | undefined> {
    if (!isPlatformBrowser(this.platformId)) return undefined;

    this.pending++;
    this._ready.set(false);
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<undefined>((resolve) => {
      timer = setTimeout(() => resolve(undefined), READY_TIMEOUT_MS);
    });
    try {
      return await Promise.race([this.resolve(part), timeout]);
    } catch (error) {
      console.error('[LanguageReady] Erro ao carregar idioma:', error);
      return undefined;
    } finally {
      clearTimeout(timer);
      if (--this.pending === 0) this._ready.set(true);
    }
  }

  private async resolve(part: string): Promise<DetectedLocale | undefined> {
    this.detection ??= this.detectAndUse();
    let detected: DetectedLocale | undefined;
    try {
      detected = await this.detection;
    } finally {
      // ponytail: se detect/use falhar, ainda carrega o part no idioma corrente (fallback en)
      await this.loadPart(part);
    }
    return detected;
  }

  private async detectAndUse(): Promise<DetectedLocale> {
    const detected = await this.languageService.detect();
    await firstValueFrom(this.translate.use(detected.lang));
    return detected;
  }

  private loadPart(part: string): Promise<void> {
    let loading = this.parts.get(part);
    if (!loading) {
      loading = this.i18n.load(part);
      this.parts.set(part, loading);
    }
    return loading;
  }
}
